import { Card, CardActions, CardContent } from '@mui/material';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useUpdateView } from '../../../../sic-lib/api/hooks/useViews';
import { GenericReducer, genericReducer } from '../../../../sic-lib/reducers/genericReducer';
import { View, buildUpdateView } from '../../../../sic-lib/types/View';
import { ViewProperty } from '../../../../sic-lib/types/ViewProperty';
import { IErrorFormValidation } from '../../../../sic-lib/utils/formValidationHelpers';
import DraggableViewProperties from '../../../Contact/ContactGroups/DraggableViewProperties/DraggableViewProperties';
import ButtonLoading from '../../../Shared/ButtonLoading';
import TextFieldFast from '../../../Shared/TextFieldFast/TextFieldFast';
import SelectViewProperties from '../SelectViewProperties/SelectViewProperties';

const useStyles = makeStyles()((theme: Theme) => ({
    btnCancel: {
        color: theme.palette.btnDefault.main,
        '&:hover': {
            backgroundColor: theme.palette.btnDefault.light,
        },
    },
}));

interface FormState {
    viewName: string;
    viewProperties: ViewProperty[];
    formError: IErrorFormValidation | null;
}

interface Props {
    config: View;
    onToggleHandler: () => void;
}

export default function ClientViewsItemEdit({ config, onToggleHandler }: Props) {
    const { classes } = useStyles();

    const { mutate: updateViewMutation, isPending: isUpdateLoading } = useUpdateView();

    const [formState, formDispatch] = React.useReducer<GenericReducer<FormState>>(genericReducer, {
        viewName: config.name,
        viewProperties: config.properties,
        formError: null,
    });

    const handleSubmit = async () => {
        try {
            const view = buildUpdateView({
                ...config,
                ...{
                    name: formState.viewName,
                    properties: formState.viewProperties,
                },
            });

            updateViewMutation(view);

            onToggleHandler();
        } catch (error: any) {
            if (error.isFormError) {
                formDispatch({ formError: error as IErrorFormValidation });
            }
        }
    };

    return (
        <ClickAwayListener onClickAway={onToggleHandler}>
            <Card>
                <CardContent>
                    <TextFieldFast
                        fieldName="viewName"
                        fieldValue={formState.viewName}
                        label="View label"
                        variant="outlined"
                        size="small"
                        fullWidth={true}
                        autoFocus={true}
                        error={Boolean(formState.formError?.field === 'name')}
                        helperText={
                            formState.formError?.field === 'name' ? formState.formError.message : ''
                        }
                        onChangeHandler={formDispatch}
                    />
                </CardContent>
                <Divider />
                <CardContent>
                    <DraggableViewProperties
                        viewProperties={formState.viewProperties}
                        onChangeHandler={formDispatch}
                    />
                    <SelectViewProperties
                        label="Add Property"
                        variant="outlined"
                        fullWidth={true}
                        SelectProps={{
                            MenuProps: { disablePortal: true },
                        }}
                        viewProperties={formState.viewProperties}
                        onSelectHandler={formDispatch}
                        error={Boolean(formState.formError?.field === 'viewProperties')}
                        helperText={
                            formState.formError?.field === 'viewProperties'
                                ? formState.formError.message
                                : ''
                        }
                    />
                </CardContent>
                <Divider />
                <CardActions>
                    <Button className={classes.btnCancel} onClick={onToggleHandler}>
                        Cancel
                    </Button>
                    <ButtonLoading
                        isLoading={isUpdateLoading}
                        onClick={handleSubmit}
                        color="secondary"
                        variant="outlined"
                    >
                        Save
                    </ButtonLoading>
                </CardActions>
            </Card>
        </ClickAwayListener>
    );
}
